import { useDesignSystemChange } from "@/shared";
import Echart from "@components/e-chart";
import type { StaticEntityType } from "@config";
import { useIsEditorContext } from "@editor-providers";
import { useDesignSystemVar } from "@library/shared/hooks";
import { useEffect, useMemo } from "react";
import { useElementOnScreen } from "../hooks";
import { ChartStyled, ChartWrapperStyled } from "../styles/Chart.styled";
import type {
  ChartProps,
  EditorDataProps,
  EditorSlotsProps,
  IChartListDynamicVarData,
} from "../types";
import {
  chartDataDynamicVariableName,
  normalizeData,
  parseChartDataDynamicVar,
  parseChartListDynamicVar,
  parser,
} from "../utils";

const EDITOR_ATTRIBUTES = ({ isEditor }: { isEditor: boolean }) => {
  const payload: Record<string, string> = {
    "data-dnd-trigger": "true",
  };
  return isEditor ? payload : {};
};

export const ChartLibrary = ({
  entity,
  editorSlots,
  editorData,
}: {
  entity: StaticEntityType;
  editorData?: EditorDataProps;
  editorSlots?: EditorSlotsProps;
}) => {
  const parsedEntity = useMemo(() => {
    const { listDynamicVariable, dataDynamicVariable } = entity.settings;

    // A `data` variable owns the WHOLE grid (version payload); checked first,
    // same as the table library.
    if (
      !entity.settings.versionStatic &&
      typeof dataDynamicVariable === "string" &&
      dataDynamicVariable
    ) {
      const matrix =
        window.__SD_DATA__?.[chartDataDynamicVariableName(dataDynamicVariable)];
      const settings = parseChartDataDynamicVar({
        settings: entity.settings as ChartProps,
        matrix,
      });
      return settings === entity.settings ? entity : { ...entity, settings };
    }

    // A static version copy owns its grid: no list binding either.
    if (!listDynamicVariable || entity.settings.versionStatic) return entity;

    const data = window.__SD_DATA__?.[
      listDynamicVariable
    ] as IChartListDynamicVarData;

    if (!data || data.data.length === 0) {
      return entity;
    }

    const settings = parseChartListDynamicVar({
      settings: entity.settings as ChartProps,
      data,
    });

    return { ...entity, settings };
  }, [entity]);

  const { settings } = parsedEntity as StaticEntityType<ChartProps>;
  const width = editorData?.width || settings.width;
  const height = editorData?.height || settings.height;
  const designSystemChange = useDesignSystemChange();
  const isEditor = useIsEditorContext();

  const fontFamily: string | undefined = useDesignSystemVar(
    "var(--paragraph-font)",
  );

  const data = useMemo(
    () => normalizeData({ data: settings.data }),
    [settings.data],
  );

  const option = useMemo(
    () =>
      parser(settings.chartType, {
        ...settings,
        data,
        fontFamily,
      }),
    [settings, fontFamily, data, designSystemChange],
  );

  const [containerRef, key, setKey] = useElementOnScreen(
    { threshold: 0.7 },
    settings.animation,
  );

  useEffect(() => {
    if (!isEditor) return;
    setKey((prev: number) => prev + 1);
  }, [designSystemChange, setKey, isEditor]);

  return (
    <ChartStyled
      onDoubleClick={editorData?.setActiveEditDialog}
      ref={(r) => {
        containerRef.current = r;
        const elementRef = editorData?.elementRef;
        if (elementRef) {
          elementRef.current = r;
        }
      }}
      $opacity={key}
      className="tw-relative tw-h-full tw-w-full tw-transition-opacity tw-duration-200 tw-ease-in-out"
    >
      <ChartWrapperStyled
        $horizontalAlignment={settings.horizontalAlignment}
        className="chart__wrapper tw-relative tw-flex tw-h-full tw-w-full tw-items-center [&_.echarts-for-react]:tw-h-full [&_.echarts-for-react]:tw-w-full"
      >
        {editorSlots?.resizeSlot}
        <div
          style={{ width: width ? width + "%" : "100%" }}
          className="tw-h-full"
          {...EDITOR_ATTRIBUTES({ isEditor: !!editorData })}
        >
          <Echart option={option.option} trigger={key} height={height} />
        </div>
      </ChartWrapperStyled>
    </ChartStyled>
  );
};
